import { z } from 'zod/v4';

/**
 * A numeric value carried as a string so the precision survives the trip from
 * the grid to the `numeric` columns (Drizzle takes numeric as a string). Empty
 * strings are rejected — callers omit the field or send `null` for "no value".
 */
const numericString = z
  .string()
  .trim()
  .min(1, 'Required')
  .refine((value) => Number.isFinite(Number(value)), 'Must be a number');

const optionalNumericString = numericString.nullish();

/** A custom (tenant-authored) owner created inline by the manual builder. */
const ownerInputSchema = z.object({
  name: z.string().trim().min(1, 'Owner name is required'),
  /** Required — an owner ALWAYS has a state (owners.state_id is NOT NULL). */
  stateId: z.string().uuid(),
});

const lettingInputSchema = z
  .object({
    /** ISO date string; defaults to "now" when omitted. */
    lettingDate: z.string().trim().min(1).nullish(),
  })
  .nullish();

const proposalInputSchema = z.object({
  /** The bid-tab identity surfaced as the proposal call number when present. */
  contractId: z.string().trim().min(1).nullish(),
  description: z.string().trim().nullish(),
});

const vendorInputSchema = z.object({
  name: z.string().trim().min(1, 'Vendor name is required'),
});

const itemInputSchema = z.object({
  itemNumber: z.string().trim().min(1, 'Item number is required'),
  description: z.string().trim().default(''),
  unit: z.string().trim().nullish(),
  quantity: numericString,
});

const cellInputSchema = z.object({
  vendorName: z.string().trim().min(1),
  itemNumber: z.string().trim().min(1),
  unitPrice: numericString,
  /** Optional; derived from unitPrice × quantity when absent. */
  extendedPrice: optionalNumericString,
});

/**
 * Input contract for persisting one manual custom bid tabulation in a single
 * transaction. The grid (rows = items, columns = vendors, cells = unit prices)
 * and the review screen's save both submit this shape. Vendor/item names are
 * matched by their grid label (`vendorName`/`itemNumber`); rows with no cell
 * for a vendor simply produce no bid_item for that vendor.
 */
export const saveCustomBidTabSchema = z.object({
  owner: ownerInputSchema,
  letting: lettingInputSchema,
  proposal: proposalInputSchema,
  vendors: z.array(vendorInputSchema).min(1, 'At least one vendor is required'),
  items: z.array(itemInputSchema).min(1, 'At least one item is required'),
  cells: z.array(cellInputSchema).default([]),
  /** Optional link back to the EDG-1176 upload session that sourced the tab. */
  uploadSessionId: z.string().uuid().nullish(),
  /**
   * Optional link back to the uploaded file this review was opened from. When
   * present, the save creates the `custom_bid_tab_files` row tying the saved
   * proposal to its source file (EDG-1210 per-file review).
   */
  fileId: z.string().uuid().nullish(),
});

export type SaveCustomBidTabInput = z.input<typeof saveCustomBidTabSchema>;
export type ParsedSaveCustomBidTabInput = z.output<
  typeof saveCustomBidTabSchema
>;

/** Result of a successful save — the created proposal (custom bid tab) id. */
export interface SaveCustomBidTabResult {
  proposalId: string;
}

/** Review states a custom bid tab can be moved to (mirrors the DB enum). */
export const customBidTabReviewStatusSchema = z.enum([
  'pending',
  'approved',
  'rejected',
]);

export type CustomBidTabReviewStatus = z.infer<
  typeof customBidTabReviewStatusSchema
>;

export const setCustomBidTabReviewStatusSchema = z.object({
  status: customBidTabReviewStatusSchema,
});

/**
 * The editable header of a custom bid tab — owner/state identity plus the
 * proposal's contract fields. Every field is optional: a write sets ONLY what's
 * provided, so the UI (and, later, integrations) can patch one field without
 * clobbering the others. This is the source-agnostic enrichment seam — the same
 * shape whether a human or a feed populates it.
 */
/** ISO date (yyyy-mm-dd) or null to clear — validated so an invalid/locale
 *  string can't reach `new Date()` and yield an Invalid Date. */
const isoDate = z
  .string()
  .trim()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected yyyy-mm-dd')
  .nullish();

export const updateCustomBidTabHeaderSchema = z.object({
  // ── Ownership + location (re-homes the proposal) ──────────────────────────
  /** Pick an EXISTING owner by id — re-homes the proposal to that owner. Takes
   *  precedence over `ownerName` when both are sent. */
  ownerId: z.string().uuid().nullish(),
  /** Owner display name — upserts a custom owner under the org (used when no
   *  `ownerId` is given: type-to-create). */
  ownerName: z.string().trim().min(1).optional(),
  /** The state the owner sits in. */
  stateId: z.string().uuid().nullish(),
  /** Districts on this proposal (replace-set → proposal_districts). */
  districtIds: z.array(z.string().uuid()).optional(),
  /** Counties on this proposal (replace-set → proposal_counties). */
  countyIds: z.array(z.string().uuid()).optional(),

  // ── Contract identity ─────────────────────────────────────────────────────
  callNumber: z.string().trim().min(1).optional(),
  contractId: z.string().trim().nullish(),
  contractTypeId: z.string().uuid().nullish(),
  projectTypeId: z.string().uuid().nullish(),
  projectNumber: z.string().trim().nullish(),
  route: z.string().trim().nullish(),
  description: z.string().trim().nullish(),

  // ── Dates + completion ────────────────────────────────────────────────────
  lettingDate: isoDate,
  completionDate: isoDate,
  completionCalendarDays: z.number().int().nonnegative().nullish(),
  completionWorkingDays: z.number().int().nonnegative().nullish(),

  // ── Value ─────────────────────────────────────────────────────────────────
  /** Engineer's cost estimate. Carried as a string so numeric precision
   *  survives the trip to the `numeric` column; null clears it. */
  costEstimate: z
    .string()
    .trim()
    .refine((v) => Number.isFinite(Number(v)), 'Must be a number')
    .nullish(),
});

export type UpdateCustomBidTabHeaderInput = z.infer<
  typeof updateCustomBidTabHeaderSchema
>;

/** Create a folder for grouping this org's custom proposals (EDG-1911). */
export const createCustomProposalFolderSchema = z.object({
  name: z.string().trim().min(1).max(255),
  description: z.string().trim().max(2048).nullish(),
  /** The folder to create this one inside. Absent or null is the top level. */
  parentId: z.string().uuid().nullish(),
});

export type CreateCustomProposalFolderInput = z.infer<
  typeof createCustomProposalFolderSchema
>;

export const updateCustomProposalFolderSchema = z
  .object({
    name: z.string().trim().min(1).max(255).optional(),
    description: z.string().trim().max(2048).nullish(),
    /** Where the folder now sits. `null` moves it to the top level. */
    parentId: z.string().uuid().nullish(),
  })
  .refine(
    (input) =>
      input.name !== undefined ||
      input.description !== undefined ||
      input.parentId !== undefined,
    'At least one field is required',
  );

export type UpdateCustomProposalFolderInput = z.infer<
  typeof updateCustomProposalFolderSchema
>;

/** Put a custom proposal in a folder, or take it out of one with `null`. */
export const setCustomProposalFolderSchema = z.object({
  folderId: z.string().uuid().nullable(),
});

/** Put many custom proposals in one folder, or take them all out with `null`.
 *  `folderId` is nullable but NOT optional, same as the single-row schema: an
 *  omitted key is a malformed request, never an unfile. */
export const bulkSetCustomProposalFolderSchema = z.object({
  customProposalIds: z.array(z.string().uuid()).min(1).max(200),
  folderId: z.string().uuid().nullable(),
});

export type BulkSetCustomProposalFolderInput = z.infer<
  typeof bulkSetCustomProposalFolderSchema
>;

/** Delete many custom proposals in one transaction. The cap is far below the
 *  move's because each id cascades through the published proposal, its bids and
 *  bid items, and the owner/vendor/item reaping. */
export const bulkDeleteCustomProposalsSchema = z.object({
  customProposalIds: z.array(z.string().uuid()).min(1).max(50),
});

export type BulkDeleteCustomProposalsInput = z.infer<
  typeof bulkDeleteCustomProposalsSchema
>;

/** Rename a custom proposal. Non-nullable and non-empty: an inline edit the
 *  user cleared is a cancel in the UI, not a request to erase the title. */
export const renameCustomProposalSchema = z.object({
  title: z.string().trim().min(1).max(255),
});

export type RenameCustomProposalInput = z.infer<
  typeof renameCustomProposalSchema
>;

/** The folder ids in the order the user arranged them. */
export const reorderCustomProposalFoldersSchema = z.object({
  folderIds: z.array(z.string().uuid()),
});

/** Create a new custom proposal — every identity field is optional;
 *  the custom proposal can start empty and be enriched later. */
export const createCustomProposalSchema = z.object({
  ownerName: z.string().trim().min(1).nullish(),
  ownerId: z.string().uuid().nullish(),
  stateId: z.string().uuid().nullish(),
  contractId: z.string().trim().min(1).nullish(),
  title: z.string().trim().min(1).nullish(),
  description: z.string().trim().nullish(),
});

export type CreateCustomProposalInput = z.infer<
  typeof createCustomProposalSchema
>;

/** Write (a partial slice of) the custom proposal's staging matrix.
 *
 *  Firm-but-tolerant contract: the top-level keys a page may send are validated
 *  by SHAPE (arrays are arrays, metadata is an object) so a structurally wrong
 *  patch is rejected at the boundary — but the schema is LOOSE, so the engine's
 *  own extra keys and the open metadata bag pass through untouched. The element
 *  shapes stay `unknown` on purpose: the engine owns {@link CustomProposalStaging},
 *  and the TS types (imported everywhere) are what pin the field contract. */
export const writeStagingSchema = z
  .object({
    vendors: z.array(z.unknown()).optional(),
    bidders: z.array(z.string()).optional(),
    bidGroups: z.array(z.unknown()).optional(),
    estimates: z.array(z.string()).optional(),
    metadata: z.record(z.string(), z.unknown()).nullable().optional(),
  })
  .loose();

export type WriteStagingInput = z.infer<typeof writeStagingSchema>;

/** Pick which extraction feeds the custom proposal's staging (v1 merge stand-in). */
export const setPrimaryExtractionSchema = z.object({
  extractionId: z.string().uuid(),
});

export type SetPrimaryExtractionInput = z.infer<
  typeof setPrimaryExtractionSchema
>;

/** Rename a schedule inside the custom proposal's staging matrix, by group + schedule
 *  index. */
export const setScheduleNameSchema = z.object({
  groupIndex: z.number().int().nonnegative(),
  scheduleIndex: z.number().int().nonnegative(),
  name: z.string().trim().min(1),
});

export type SetScheduleNameInput = z.infer<typeof setScheduleNameSchema>;

/** Rename a bid group inside the custom proposal's staging matrix, by group index. */
export const setGroupNameSchema = z.object({
  groupIndex: z.number().int().nonnegative(),
  name: z.string().trim().min(1),
});

export type SetGroupNameInput = z.infer<typeof setGroupNameSchema>;

/** The editable header of a custom bid tab — owner/state identity + contract,
 *  as seeded into the enrichment form (the proposal stores only `owner_id`). */
export interface CustomBidTabHeader {
  proposalId: string;
  ownerId: string | null;
  ownerName: string | null;
  stateId: string | null;
  stateAbbreviation: string | null;
  districtIds: string[];
  countyIds: string[];
  callNumber: string | null;
  contractId: string | null;
  contractType: string | null;
  contractTypeId: string | null;
  projectType: string | null;
  projectTypeId: string | null;
  projectNumber: string | null;
  route: string | null;
  description: string | null;
  /** ISO date (yyyy-mm-dd) or null. */
  lettingDate: string | null;
  completionDate: string | null;
  completionCalendarDays: number | null;
  completionWorkingDays: number | null;
  /** Numeric string (matches the `numeric` column) or null. */
  costEstimate: string | null;
}
